import { LitElement, html, css, nothing, render } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { parseTemplate } from '../lib/template-parser.js';
import { focusHashTarget, renderContentElement, renderContentElements, selectDraftModule } from './site-page-renderer.js';
import { pageContentStyles } from '../styles/page-content.css.js';
import { browserUrl, isDraftPath, withBuildPrefix } from '../lib/build-paths.js';

const SLOT_FITS = ['cover', 'contain', 'center', 'norepeat'];

export class SiteTemplateRenderer extends LitElement {
  static properties = {
    template: { type: String },
    content: { type: Object },
    title: { type: String },
    _parts: { state: true },
  };

  static styles = [
    css`
      :host {
        display: block;
      }
      .template-root {
        display: contents;
      }
      .oc-marker {
        display: contents;
        font-style: normal;
      }
      .slot {
        position: relative;
        display: block;
        width: 100%;
      }
      .slot--image,
      .slot--empty {
        height: 100%;
        min-height: inherit;
      }
      .slot--empty {
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 1rem;
        box-sizing: border-box;
        background: linear-gradient(135deg, #dfe6ee 0%, #c3cfdb 50%, #aebdcd 100%);
      }
      .slot__label {
        font-size: 0.8rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #4a5a6d;
        opacity: 0.75;
      }
      .slot--image {
        background-position: center;
        background-repeat: no-repeat;
      }
      .slot--fit-cover {
        background-size: cover;
        background-position: center;
      }
      .slot--fit-contain {
        background-size: contain;
        background-position: center;
      }
      .slot--fit-center {
        background-size: auto;
        background-position: center;
      }
      .slot--fit-norepeat {
        background-size: auto;
        background-position: top left;
      }
      .slot__edit-handle {
        position: absolute;
        z-index: 10000;
        top: 6px;
        left: 6px;
        max-width: calc(100% - 12px);
        padding: 5px 8px;
        overflow: hidden;
        border: 1px solid #fff;
        border-radius: 6px;
        color: #fff;
        background: #2563eb;
        box-shadow: 0 2px 10px rgba(15, 23, 42, .35);
        cursor: pointer;
        font: 700 11px/1.2 system-ui, sans-serif;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
    pageContentStyles,
  ];

  constructor() {
    super();
    this.template = 'built-in';
    this.content = null;
    this.title = '';
    this._parts = null;
    this._markers = [];
    this._error = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadTemplate();
  }

  async _loadTemplate() {
    if (!this.template || this.template === 'built-in' || !this.content) return;
    try {
      const resp = await fetch(withBuildPrefix(`/templates/${this.template}.html`));
      if (!resp.ok) throw new Error(`Template not found: ${this.template}`);
      const htmlText = await resp.text();
      this._parts = parseTemplate(htmlText);
      this._error = false;
    } catch {
      this._error = true;
      this._parts = null;
    }
  }

  render() {
    if (!this.template || this.template === 'built-in') {
      return this._renderFallback();
    }
    if (!this._parts) {
      return html`<div class="template-loading"></div>`;
    }

    // Render the whole template as ONE HTML fragment so the browser keeps
    // the nesting around placeholders intact. Components and page metadata
    // are mounted into inert markers afterwards (see _renderMarkers).
    const { combined, markers } = this._combineParts(this._parts);
    this._markers = markers;
    return html`<div class="template-root">${unsafeHTML(combined)}</div>`;
  }

  updated(changed) {
    if (changed.has('template') || changed.has('content')) {
      const isInitial = changed.get('template') === undefined && changed.get('content') === undefined;
      if (isInitial) return;
      this._parts = null;
      this._error = false;
      this._loadTemplate();
    }
    if (changed.has('_parts') || changed.has('content')) {
      this._renderMarkers();
      this._rewriteTemplateUrls();
      focusHashTarget(this.renderRoot);
      if (isDraftPath() && window.parent !== window) {
        window.parent.postMessage({ type: 'sitecms:preview-ready' }, location.origin);
      }
    }
  }

  // Template files store logical paths. Add the active build at render time;
  // draft page links additionally stay inside the authenticated preview.
  _rewriteTemplateUrls() {
    if (!this._parts) return;
    const isDraft = isDraftPath();
    for (const anchor of this.shadowRoot.querySelectorAll('a[href^="/"]')) {
      const href = anchor.getAttribute('href');
      if (isDraft && !href.startsWith('/drafts') && !href.startsWith('/api') && !href.startsWith('/uploads')) {
        anchor.setAttribute('href', withBuildPrefix('/drafts' + href));
      } else anchor.setAttribute('href', browserUrl(href));
    }
    for (const element of this.shadowRoot.querySelectorAll('[src^="/"], link[href^="/"]')) {
      const attribute = element.hasAttribute('src') ? 'src' : 'href';
      element.setAttribute(attribute, browserUrl(element.getAttribute(attribute)));
    }
  }

  _combineParts(parts) {
    let combined = '';
    const markers = [];
    for (const part of parts) {
      if (part.type === 'html') {
        combined += part.html;
      } else {
        combined += `<i class="oc-marker" data-oc-marker="${markers.length}"></i>`;
        markers.push(part);
      }
    }
    return { combined, markers };
  }

  _renderMarkers() {
    if (!this._parts || !this._markers) return;
    for (let i = 0; i < this._markers.length; i += 1) {
      const marker = this.shadowRoot.querySelector(`[data-oc-marker="${i}"]`);
      if (marker) {
        render(this._renderPart(this._markers[i]), marker);
      }
    }
  }

  _renderPart(part) {
    switch (part.type) {
      case 'html':
        return html`${unsafeHTML(part.html)}`;
      case 'page-meta':
        return this._escapeHtml(this._getMeta(part.key));
      case 'component':
        return this._renderComponent(part.componentType, part.data);
      default:
        return '';
    }
  }

  _renderComponent(type, data) {
    switch (type) {
      case 'page-content':
        return this._renderPageContent();
      case 'header':
        return html`<site-header></site-header>`;
      case 'footer':
        return html`<site-footer></site-footer>`;
      case 'hero-slider':
        return html`<site-hero-slider .slides=${data.slides || []}></site-hero-slider>`;
      case 'slot':
        return this._renderSlot(data);
      default:
        return '';
    }
  }

  _renderSlot(data) {
    const id = typeof data.element === 'string' ? data.element : '';
    const label = data.label || id || 'Content area';
    const fit = SLOT_FITS.includes(data.fit) ? data.fit : 'cover';
    const element = id ? this._findElement(this.content?.elements || [], id) : null;
    const hasImage = element?.type === 'image' && Boolean(element.props?.src);

    if (!element) {
      return html`<div class="slot slot--empty" role="img" aria-label=${label}>
        ${isDraftPath() ? html`<button type="button" class="slot__edit-handle" aria-label=${`Add content to ${label}`} @click=${(event) => this._selectEmptySlot(event, id, label)}>Edit ${label}</button>` : nothing}
        <span class="slot__label">${label}</span>
      </div>`;
    }

    if (hasImage) {
      const alt = element.props.alt || label;
      return html`<div class="slot slot--image slot--fit-${fit}" data-cms-module-id=${element.id}
        style="background-image:url('${browserUrl(element.props.src)}')" role="img" aria-label=${alt}>
        ${isDraftPath() ? html`<button type="button" class="slot__edit-handle" aria-label=${`Edit ${label}`} @click=${(event) => selectDraftModule(event, element.id)}>Edit ${label}</button>` : nothing}
      </div>`;
    }

    if (element.type === 'image') {
      return html`<div class="slot slot--empty" data-cms-module-id=${element.id} role="img" aria-label=${label}>
        ${isDraftPath() ? html`<button type="button" class="slot__edit-handle" aria-label=${`Edit ${label}`} @click=${(event) => selectDraftModule(event, element.id)}>Edit ${label}</button>` : nothing}
        <span class="slot__label">${label}</span></div>`;
    }

    return html`<div class="slot slot--content">${isDraftPath() ? html`<button type="button" class="slot__edit-handle" aria-label=${`Edit ${label}`} @click=${(event) => selectDraftModule(event, element.id)}>Edit ${label}</button>` : nothing}${renderContentElement(element)}</div>`;
  }

  _selectEmptySlot(event, id, label) {
    if (!id || window.parent === window || !isDraftPath()) return;
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    window.parent.postMessage({ type: 'sitecms:slot-selected', moduleId: id, label }, location.origin);
  }

  _findElement(elements, id) {
    for (const element of elements) {
      if (element.id === id) return element;
      const found = this._findElement(element.children || [], id);
      if (found) return found;
    }
    return null;
  }

  _renderPageContent() {
    const elements = this.content?.elements || [];
    const meta = this.content?.meta || {};
    const hasHero = elements.some((el) => el.type === 'slideshow');
    return html`
      <article class="page">
        ${hasHero ? '' : html`
          <header class="page__header">
            <h1 class="page__title">${meta.headline || this.title || ''}</h1>
            ${meta.subtitle ? html`<p class="page__subtitle">${meta.subtitle}</p>` : ''}
          </header>
        `}
        <div class="elements ${hasHero ? 'elements--flush' : ''}">
          ${renderContentElements(elements)}
        </div>
      </article>
    `;
  }

  _renderFallback() {
    return html`<site-page-renderer .content=${this.content} .title=${this.title}></site-page-renderer>`;
  }

  _getMeta(key) {
    if (!this.content?.meta) return '';
    const value = this.content.meta[key];
    return value != null ? String(value) : '';
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

customElements.define('site-template-renderer', SiteTemplateRenderer);
